import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Award, Clock, DollarSign } from 'lucide-react';
import CareerCard from './CareerCard';
import { getPerformanceLevel } from '../data/careerData';

const ResultsSection = ({ recommendations, userData, onReset }) => {
  const performanceLevel = getPerformanceLevel(userData.marks);
  
  const getPerformanceMessage = () => {
    switch (performanceLevel) {
      case 'high':
        return {
          title: 'Excellent Performance! 🎉',
          message: 'Your high marks open up many prestigious career opportunities. You have excellent prospects in competitive fields.',
          color: '#10b981'
        };
      case 'medium':
        return {
          title: 'Good Performance! 👍',
          message: 'Your solid academic performance provides good career options. Focus on developing relevant skills to enhance your prospects.',
          color: '#3b82f6'
        };
      case 'low':
        return {
          title: 'Keep Improving! 💪',
          message: 'While your marks may limit some options, there are still many rewarding career paths. Focus on skill development and practical experience.',
          color: '#f59e0b'
        };
      default:
        return {
          title: 'Career Recommendations',
          message: 'Here are some career options based on your profile.',
          color: '#6b7280'
        };
    }
  };

  const performanceInfo = getPerformanceMessage();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      {/* Performance Summary */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="card"
        style={{
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
          padding: '2rem',
          margin: '1rem 0',
          borderLeft: `4px solid ${performanceInfo.color}`
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
          <TrendingUp size={24} color={performanceInfo.color} />
          <h2 style={{ 
            fontSize: '1.5rem', 
            fontWeight: '600', 
            color: '#333',
            marginLeft: '0.75rem'
          }}>
            {performanceInfo.title}
          </h2>
        </div>
        
        <p style={{ color: '#666', marginBottom: '1.5rem', lineHeight: '1.6' }}>
          {performanceInfo.message}
        </p>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '1rem',
          marginBottom: '1rem'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            padding: '0.75rem',
            background: '#f8fafc',
            borderRadius: '8px'
          }}>
            <Award size={20} color="#667eea" />
            <div>
              <p style={{ fontSize: '0.875rem', color: '#666', margin: 0 }}>Stream</p>
              <p style={{ fontSize: '1rem', fontWeight: '600', color: '#333', margin: 0 }}>
                {userData.stream.charAt(0).toUpperCase() + userData.stream.slice(1)}
              </p>
            </div>
          </div>
          
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            padding: '0.75rem',
            background: '#f8fafc',
            borderRadius: '8px'
          }}>
            <DollarSign size={20} color="#667eea" />
            <div>
              <p style={{ fontSize: '0.875rem', color: '#666', margin: 0 }}>Marks</p>
              <p style={{ fontSize: '1rem', fontWeight: '600', color: '#333', margin: 0 }}>
                {userData.marks}%
              </p>
            </div>
          </div>
          
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            padding: '0.75rem',
            background: '#f8fafc',
            borderRadius: '8px'
          }}>
            <Clock size={20} color="#667eea" />
            <div>
              <p style={{ fontSize: '0.875rem', color: '#666', margin: 0 }}>Level</p>
              <p style={{ fontSize: '1rem', fontWeight: '600', color: '#333', margin: 0 }}>
                {performanceLevel.charAt(0).toUpperCase() + performanceLevel.slice(1)}
              </p>
            </div>
          </div>
        </div>

        {userData.interests.length > 0 && (
          <div>
            <p style={{ fontSize: '0.875rem', color: '#666', marginBottom: '0.5rem' }}>
              <strong>Your Interests:</strong>
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {userData.interests.map((interest, idx) => (
                <span
                  key={idx}
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '20px',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}
                >
                  {interest}
                </span>
              ))}
            </div>
          </div>
        )}
      </motion.div>

      {/* Career Recommendations */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
      >
        <h3 style={{ 
          fontSize: '1.75rem', 
          fontWeight: '600', 
          color: '#333',
          marginBottom: '1rem',
          textAlign: 'center'
        }}>
          Recommended Career Paths
        </h3>
        
        <div className="grid">
          {recommendations.map((career, index) => (
            <CareerCard key={career.id} career={career} index={index} />
          ))}
        </div>
      </motion.div>

      {/* Action Buttons */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6 }}
        style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: '1rem',
          marginTop: '2rem',
          flexWrap: 'wrap'
        }}
      >
        <motion.button
          onClick={onReset}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          style={{
            background: 'white',
            color: '#667eea',
            border: '2px solid #667eea',
            padding: '12px 24px',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
        >
          Try Different Input
        </motion.button>
        
        <motion.button
          onClick={() => window.print()}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
        >
          Print Results
        </motion.button>
      </motion.div>

      {/* Additional Tips */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.8 }}
        className="card"
        style={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          borderRadius: '16px',
          padding: '1.5rem',
          margin: '2rem 0',
          border: '1px solid #e2e8f0'
        }}
      >
        <h4 style={{ 
          fontSize: '1.25rem', 
          fontWeight: '600', 
          color: '#333',
          marginBottom: '1rem'
        }}>
          💡 Pro Tips for Success
        </h4>
        <ul style={{ 
          color: '#666', 
          lineHeight: '1.8',
          paddingLeft: '1.5rem'
        }}>
          <li>Research each career option thoroughly before making a decision</li>
          <li>Consider pursuing internships or certifications in your chosen field</li>
          <li>Network with professionals in your target industry</li>
          <li>Stay updated with industry trends and technological advancements</li>
          <li>Focus on developing both technical and soft skills</li>
        </ul>
      </motion.div>
    </motion.div>
  );
};

export default ResultsSection; 